import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { CursorType, setCursorType } from "@/store/globalSettingsSlice";
import { useIframePoolStore } from "@/components/Canvas/IframePool/iframePoolStore";
import type { Storyboard as StoryboardType } from "@/utils/types";

/**
 * Hook for managing fullscreen mode functionality.
 * Centralizes all fullscreen logic including entering, exiting, and state management.
 */
export const useFullScreenMode = () => {
  const dispatch = useDispatch();

  const { setFullscreenStoryboard, fullscreenStoryboardId } =
    useIframePoolStore();

  const isFullScreenEnabled = fullscreenStoryboardId !== null;

  const enterFullscreen = useCallback(
    (storyboard: StoryboardType) => {
      setFullscreenStoryboard(storyboard.id);

      dispatch(
        setCursorType({
          cursorType: CursorType.Drive,
          fromKeyDown: false,
        }),
      );
    },
    [dispatch, setFullscreenStoryboard],
  );

  const exitFullscreen = useCallback(() => {
    if (!fullscreenStoryboardId) {
      console.warn("No storyboard is currently in fullscreen mode");
      return;
    }

    dispatch(
      setCursorType({
        cursorType: CursorType.Pointer,
        fromKeyDown: false,
      }),
    );

    setFullscreenStoryboard(null);
  }, [fullscreenStoryboardId, setFullscreenStoryboard, dispatch]);

  const toggleFullscreen = useCallback(
    (storyboard: StoryboardType) => {
      if (fullscreenStoryboardId === storyboard.id) {
        exitFullscreen();
      } else {
        enterFullscreen(storyboard);
      }
    },
    [fullscreenStoryboardId, enterFullscreen, exitFullscreen],
  );

  const isInFullScreenMode = useCallback(
    (storyboardId: string) => {
      return fullscreenStoryboardId === storyboardId;
    },
    [fullscreenStoryboardId],
  );

  return {
    isFullScreenEnabled,
    fullscreenStoryboardId,
    enterFullscreen,
    exitFullscreen,
    toggleFullscreen,
    isInFullScreenMode,
  };
};
